/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { ISmartTaskBinding, ITaskContainer } from 'app/core/components/tasks/task';
import { AssessmentService } from 'app/core/services/assessment-service';
import { apiCall } from 'app/shared/lib/api/api';
import { IAssessmentAttachmentOptions } from 'app/core/components/assessments/assessment-attachment';
import * as Core from 'app/core/models/essette-api';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import * as CD from "app/cd/models/essette-api";


/** View-Model for the Assessment Smart-Task component */
@autoinject()
export class SmartAssessment extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    protected task!: Core.Task;
    protected target: {
        id: string;
        number: string | null | undefined;
        member: CD.Member;
    } | null = null;
    protected targetId!: string;
    protected tasksConfig: ITasksConfig = {};
    protected assessmentsConfig: IAssessmentsConfig = {};

    private container!: ITaskContainer;
    protected readonly: boolean = false;

    protected attachmentId: string | null = null;
    protected options: IAssessmentAttachmentOptions | null = null;


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AssessmentService,
        private readonly context: CasePageContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(params: ISmartTaskBinding): Promise<void> {
        this.logger.debug('activate', params);

        if(!params || !params.container || !params.targetId || !params.task || !params.task.id || !params.tasksConfig || !params.tasksConfig.smartTasksConfig || !params.tasksConfig.smartTasksConfig.assessmentsConfig) {
            throw TypeError(`${SmartAssessment.name} Component Model not Bound.`);
        }

        this.targetId = params.targetId;
        this.tasksConfig = params.tasksConfig;
        this.target = {id: this.targetId, member: params.member, number: this.context.case.number};

        this.options = {
            showHeader: true,
            showToc: false,
            showBack: false,
            showActions: true,
            showMember: true,
            showMemberSummaryLink: true,
            showTarget: true,
            showTargetSummaryLink: true,
            forceRequiredQuestions: false,
            canReopen: true,
            stickyActions: false,
            isTaskDisabled: params.isDisabled ?? false
        };

        this.assessmentsConfig = {
            ...params.tasksConfig.smartTasksConfig.assessmentsConfig,
        };

        this.state = params.state || this.state;

        this.task = params.task;
        this.container = params.container;
        // override readonly unless the smart task is disabled (from case being closed)
        this.readonly = params.isDisabled ? params.readonly : false;

        this.service.configure(this.assessmentsConfig);
        await this.loadAttachment(this.task.id!);
    }


    ////****************** Events ***************************************************////
    protected completeAssessment(): void {
        if(this.container) {
            this.container.completeTask(true);
        }
    }


    ////****************** Functions ************************************************////


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAttachment(taskId: string, targetId?: string): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadAttachmentByTask(taskId, undefined, targetId);
            this.attachmentId = response.results[0].id || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
